'use client';

import { memo } from 'react';
import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { buildRoute, localitiesConfig } from '@headless-workspace/config';
import { DropDownOption, GlowDropDownSearch, Icons } from '@headless-workspace/glow-ds';
import { ComponentMemo } from '@headless-workspace/typings';
import { useDebounceCallback } from 'usehooks-ts';
import { useAppLocale, useNavigation } from '../../../../../src';
import { useStoreAutocomplete } from './useStoreAutocomplete';
import { useUserFormattedAddress } from './useUserFormattedAddress';

const STORE_SEARCH_ID = 'store-search';

type StoreSearchContainerProps = {
    isHoveredOutSide?: boolean;
};

export const StoreSearchContainer: ComponentMemo<StoreSearchContainerProps> = memo(({ isHoveredOutSide }) => {
    const t = useTranslations('Header.storesAndServices');
    const locale = useAppLocale();
    const navigation = useNavigation();
    const getUserFormattedAddress = useUserFormattedAddress();
    const [searchInput, setSearchInput] = useState<string>();

    const { localitiesResults, searchLocalities, isLoading } = useStoreAutocomplete();
    const searchLocalitiesDebounced = useDebounceCallback(searchLocalities, localitiesConfig.debounceTime, {
        leading: true,
    });

    const searchLocations = useCallback(
        (input: string) => {
            setSearchInput(input);
            searchLocalitiesDebounced(input);
        },
        [searchLocalitiesDebounced, setSearchInput],
    );

    const searchLocationsNearUser = useCallback(() => {
        getUserFormattedAddress(searchLocations);
    }, [getUserFormattedAddress, searchLocations]);

    const navigateToLocation = ({ label }: DropDownOption) => {
        const storeRedirectUrl = buildRoute(locale, 'allStores', { address: label });
        navigation.push(storeRedirectUrl);
    };

    return (
        <div className={'relative flex flex-col'}>
            <GlowDropDownSearch
                id={STORE_SEARCH_ID}
                defaultValue={searchInput}
                optionsList={localitiesResults}
                title={t('banner.label.location')}
                ariaLabelInput={t('banner.label.placeholder')}
                helperLabel={t('banner.label.placeholder')}
                rightSearchInputIcon={Icons.Location}
                isLoading={isLoading}
                isHoveredOutside={isHoveredOutSide}
                onSearchChange={searchLocations}
                onSearchRightIconSubmit={searchLocationsNearUser}
                handleOptionClick={navigateToLocation}
            />
        </div>
    );
});

StoreSearchContainer.displayName = 'StoreSearchContainer';
